import ApplicationController from "./application_controller"
import { attachLexiqamusOttomanKeyboardDrag } from "./lexiqamus_search/ottoman_keyboard"

// Arabic letter to family name, same shape as OTTOMAN_KEY_FAMILIES in the
// on-screen keyboard helper. tagKeyFamilies writes the name onto data-family
// so the cell CSS can use that family's ground. Elif is listed here because
// the advanced board colors it; the basic board CSS keeps elif plain.
const DECODER_LETTER_FAMILIES = {
  "ا": "elif", "آ": "elif", "أ": "elif", "إ": "elif",
  "ٮ": "be", "ب": "be", "پ": "be", "ت": "be", "ث": "be",
  "ج": "cim", "چ": "cim", "ح": "cim", "خ": "cim",
  "د": "dal", "ذ": "dal",
  "ر": "re", "ز": "re", "ژ": "re",
  "س": "sin", "ش": "sin",
  "ص": "sad", "ض": "sad",
  "ط": "ti", "ظ": "ti",
  "ع": "ayn", "غ": "ayn",
  "ف": "fe", "ق": "fe", "ڡ": "fe",
  "ك": "kef", "گ": "kef", "ڭ": "kef",
  "و": "vav", "ؤ": "vav",
  "ه": "he", "ة": "he", "ۀ": "he", "ھ": "he", "ە": "he",
  "ی": "ye", "ئ": "ye"
}

// Star keys store a data-char code, not an Arabic letter. Each code maps to
// the family it sits with so tagKeyFamilies can set data-family and the CSS
// paints the deeper shade of that family.
// 0/1/2 tooth (be), 5 cim, 7 dal, 8 re, 9 sin, Ø sad, Ù ti, 6 ayn, Ú fe, Û kef.
const DECODER_STAR_FAMILIES = {
  "0": "be", "1": "be", "2": "be",
  "5": "cim",
  "7": "dal",
  "8": "re",
  "9": "sin",
  "Ø": "sad",
  "Ù": "ti",
  "6": "ayn",
  "Ú": "fe",
  "Û": "kef"
}

// Visual Arabic grid keyboard for the Word Decoder panel.
// Floats as a fixed overlay below the letter grid; opens on letter-input focus.
// Closes on outside click or the × button. Draggable via topbar handle.
export default class extends ApplicationController {
  static targets = [
    "panel",         // Keyboard panel root
    "dragHandle",    // Topbar drag handle
    "basicPanel",    // Basic layout panel
    "advancedPanel"  // Advanced layout panel
  ]

  // Wires drag, outside-click close, and resize reposition.
  connect() {
    this._scrollLocked = false
    this._userMovedPanel = false
    this._ensureVisibleTimer = null
    this.removeOttomanDrag = null
    this.onDocPointerdown = this.onDocPointerdown.bind(this)
    this.onFollowScroll = this.onFollowScroll.bind(this)
    this.repositionForViewport = this.repositionForViewport.bind(this)

    document.addEventListener("pointerdown", this.onDocPointerdown, true)
    window.addEventListener("scroll", this.onFollowScroll, { passive: true })
    window.addEventListener("resize", this.repositionForViewport)

    if (this.hasPanelTarget && this.hasDragHandleTarget) {
      this.onDragHandlePointerdown = (event) => {
        if (event.target.closest("button")) return
        this._userMovedPanel = true
      }
      this.dragHandleTarget.addEventListener("pointerdown", this.onDragHandlePointerdown, true)

      this.removeOttomanDrag = attachLexiqamusOttomanKeyboardDrag(
        this.panelTarget,
        this.dragHandleTarget,
        "lexiqamusGridKbdPosition"
      )
    }

    this.tagKeyFamilies()
    this.observeEngineCore()
  }

  // Sets data-family on each cell so the stylesheet can paint that family's ground.
  // Star keys use the deeper shade of the same family.
  tagKeyFamilies() {
    if (!this.hasPanelTarget) return
    this.panelTarget.querySelectorAll(".lq-gk-cell").forEach((cell) => {
      if (cell.classList.contains("lq-gk-cell--zws")) return
      const token = cell.dataset.char || ""
      if (cell.classList.contains("lq-gk-cell--star")) {
        const family = DECODER_STAR_FAMILIES[token]
        if (!family) return
        cell.dataset.family = family
        return
      }
      const family = DECODER_LETTER_FAMILIES[token]
      if (family) cell.dataset.family = family
    })
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.onDocPointerdown, true)
    window.removeEventListener("scroll", this.onFollowScroll)
    window.removeEventListener("resize", this.repositionForViewport)
    if (this.hasDragHandleTarget && this.onDragHandlePointerdown) {
      this.dragHandleTarget.removeEventListener("pointerdown", this.onDragHandlePointerdown, true)
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    if (this.removeOttomanDrag) {
      this.removeOttomanDrag()
      this.removeOttomanDrag = null
    }
    this.clearEnsureVisibleTimer()
  }

  // ── Open / close ──────────────────────────────────────────────────────────

  // Shows keyboard below letter grid and positions under the focused input.
  open(nearEl) {
    if (!this.hasPanelTarget) return
    this._scrollLocked = false
    this._pageScrollEnabled = false
    this._userMovedPanel = false
    if (this._kbdSpacer) this._kbdSpacer.style.height = "0px"
    this.panelTarget.classList.remove("lq-grid-kbd--needs-page-scroll")
    this.panelTarget.classList.add("lq-grid-kbd--open", "js-lq-grid-kbd--open")
    this.panelTarget.setAttribute("aria-hidden", "false")
    requestAnimationFrame(() => {
      this.positionNear(nearEl)
      this.scrollOverflowIfNeeded()
      this.clearEnsureVisibleTimer()
      this._ensureVisibleTimer = setTimeout(() => this.scrollOverflowIfNeeded(), 320)
    })
  }

  // Hides keyboard and resets basic/advanced panel slide position.
  close() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.remove("lq-grid-kbd--open", "js-lq-grid-kbd--open", "lq-grid-kbd--needs-page-scroll")
    this.panelTarget.setAttribute("aria-hidden", "true")
    this._scrollLocked = false
    this._pageScrollEnabled = false
    this.clearEnsureVisibleTimer()
    if (this._kbdSpacer) this._kbdSpacer.style.height = "0px"
    if (this.hasBasicPanelTarget && this.hasAdvancedPanelTarget) {
      this.basicPanelTarget.classList.remove("lq-gk-panel--hidden-right")
      this.advancedPanelTarget.classList.add("lq-gk-panel--hidden-left")
    }
  }

  // True when the floating grid keyboard panel is visible.
  isOpen() {
    return this.hasPanelTarget && this.panelTarget.classList.contains("js-lq-grid-kbd--open")
  }

  // ── Panel navigation ──────────────────────────────────────────────────────

  // Slides from basic to advanced letter layout panel.
  showAdvancedPointerdown(event) {
    event.preventDefault()
    if (!this.hasBasicPanelTarget || !this.hasAdvancedPanelTarget) return
    this.basicPanelTarget.classList.add("lq-gk-panel--hidden-right")
    this.advancedPanelTarget.classList.remove("lq-gk-panel--hidden-left")
    requestAnimationFrame(() => this.repositionForViewport())
  }

  // Slides back from advanced to basic letter layout panel.
  showBasicPointerdown(event) {
    event.preventDefault()
    if (!this.hasBasicPanelTarget || !this.hasAdvancedPanelTarget) return
    this.basicPanelTarget.classList.remove("lq-gk-panel--hidden-right")
    this.advancedPanelTarget.classList.add("lq-gk-panel--hidden-left")
    requestAnimationFrame(() => this.repositionForViewport())
  }

  // ── Key insertion ─────────────────────────────────────────────────────────

  // Inserts letter or starred wildcard from a grid cell into the focused input.
  cellPointerdown(event) {
    event.preventDefault()
    const cell = event.currentTarget
    const char = cell.dataset.char
    if (!char) return

    this.dispatchGridEvent("insert", { char, svgHtml: this.cloneCellSvgHtml(cell) })
  }

  // Clones cell SVG for overlay display in the letter input box.
  cloneCellSvgHtml(element) {
    const innerSvg = element.querySelector(".js-lq-gk-cell-svg") || element.querySelector(":scope > svg")
    if (!innerSvg) return null
    const clone = innerSvg.cloneNode(true)
    clone.setAttribute("width", "44")
    clone.setAttribute("height", "44")
    return clone.outerHTML
  }

  // Inserts top-bar * or ∞ wildcard into the focused letter input.
  wildcardPointerdown(event) {
    event.preventDefault()
    const button = event.currentTarget
    const char = button.dataset.char
    if (!char) return

    let svgHtml = this.cloneCellSvgHtml(button)
    // The ∞ key is a text glyph, so there is no cell SVG to clone. Draw it the
    // same way the other decoder cells draw a letter.
    if (!svgHtml && char === "∞") {
      svgHtml = '<svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true"><text x="22" y="28" text-anchor="middle" font-family="Noto Naskh Arabic, serif" font-size="24" font-weight="700" fill="#9d1c51">∞</text></svg>'
    }

    this.dispatchGridEvent("insert", { char, svgHtml })
  }

  // Forwards backspace to word-decoder letter grid handler.
  backspacePointerdown(event) {
    event.preventDefault()
    this.dispatchGridEvent("backspace")
  }

  // Forwards clear-all to reset the entire letter grid.
  clearAllPointerdown(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dispatchGridEvent("clear-all")
  }

  // Closes keyboard via the × button in the top bar.
  closePointerdown(event) {
    event.preventDefault()
    this.close()
  }

  // Helpers

  // Stimulus 2 has no this.dispatch(); emit namespaced events for data-action wiring.
  dispatchGridEvent(name, detail = {}) {
    this.element.dispatchEvent(new CustomEvent(`lexiqamus-word-decoder-keyboard:${name}`, {
      bubbles: true,
      cancelable: false,
      detail
    }))
  }

  // Centers keyboard under engine core, or restores saved drag position.
  positionNear(nearEl) {
    if (!this.hasPanelTarget) return

    const panel = this.panelTarget
    const saved = this.readSavedPosition()
    if (saved) {
      panel.style.top       = saved.top
      panel.style.left      = saved.left
      panel.style.right     = "auto"
      panel.style.transform = "none"
      this.clampPanelToViewport(panel)
      return
    }

    const viewportEdgePadding = 8
    const viewportHeight      = window.innerHeight
    const anchorRect          = this.engineRect() || nearEl?.getBoundingClientRect?.() || panel.getBoundingClientRect()
    const panelWidth          = panel.offsetWidth || 460
    const panelHeight         = panel.offsetHeight || 340
    const panelLeft           = Math.max(
      viewportEdgePadding,
      Math.min(anchorRect.left + anchorRect.width / 2 - panelWidth / 2, window.innerWidth - panelWidth - viewportEdgePadding)
    )

    panel.style.left = `${panelLeft}px`

    let topBelowAnchor = anchorRect.bottom + 2
    if (!this._scrollLocked) {
      const bottomOverflowPx = (topBelowAnchor + panelHeight) - (viewportHeight - viewportEdgePadding)
      if (bottomOverflowPx > 0) {
        this.releaseHomeViewportLock()
        const desiredScrollY    = Math.max(0, window.scrollY + anchorRect.top - 14)
        const maxNaturalScrollY = Math.max(0, document.documentElement.scrollHeight - viewportHeight)
        const spacer            = this.kbdSpacerEl()
        if (desiredScrollY > maxNaturalScrollY) {
          spacer.style.height = `${desiredScrollY - maxNaturalScrollY + 24}px`
        }
        if (desiredScrollY > window.scrollY) {
          window.scrollTo(0, desiredScrollY)
        }
        const anchorRectAfterScroll = this.engineRect() || anchorRect
        topBelowAnchor = anchorRectAfterScroll.bottom + 2
      }
      this._scrollLocked = true
    }

    const clampedTop = Math.max(viewportEdgePadding, Math.min(topBelowAnchor, viewportHeight - panelHeight - viewportEdgePadding))
    panel.style.top       = `${clampedTop}px`
    panel.style.right     = "auto"
    panel.style.transform = "none"
  }

  // Glue keyboard under engine (no upward clamp).
  followUnderEngine() {
    if (!this.hasPanelTarget || !this.autoFollowEnabled()) return

    const anchorRect = this.engineRect()
    if (!anchorRect) return

    const panel            = this.panelTarget
    const viewportEdgePadding = 8
    const panelWidth       = panel.offsetWidth || 488
    const panelLeft        = Math.max(
      viewportEdgePadding,
      Math.min(anchorRect.left + anchorRect.width / 2 - panelWidth / 2, window.innerWidth - panelWidth - viewportEdgePadding)
    )
    panel.style.top  = `${anchorRect.bottom + 4}px`
    panel.style.left = `${panelLeft}px`
    panel.style.right     = "auto"
    panel.style.transform = "none"
  }

  // Scroll/page-spacer when keyboard bottom clips the viewport (first open only; lock prevents repeats).
  scrollOverflowIfNeeded() {
    if (!this.isOpen() || !this.hasPanelTarget) return

    const bottomOverflowPx = this.panelTarget.getBoundingClientRect().bottom - (window.innerHeight - 10)
    if (bottomOverflowPx <= 0) return

    this.releaseHomeViewportLock()
    const spacer              = this.kbdSpacerEl()
    const maxNaturalScrollY   = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    const extraSpacerHeight   = (window.scrollY + bottomOverflowPx) - maxNaturalScrollY
    if (extraSpacerHeight > 0) {
      const currentSpacerHeight = parseInt(spacer.style.height || "0", 10) || 0
      spacer.style.height = `${currentSpacerHeight + Math.ceil(extraSpacerHeight + 20)}px`
    }
    window.scrollBy({ top: Math.ceil(bottomOverflowPx), behavior: "instant" })
  }

  clearEnsureVisibleTimer() {
    if (!this._ensureVisibleTimer) return
    clearTimeout(this._ensureVisibleTimer)
    this._ensureVisibleTimer = null
  }

  onFollowScroll() {
    if (!this.isOpen() || !this.autoFollowEnabled()) return
    this.followUnderEngine()
  }

  repositionForViewport() {
    if (!this.isOpen()) return
    if (this.autoFollowEnabled()) {
      this.positionNear()
      if (!this._scrollLocked) this.scrollOverflowIfNeeded()
      return
    }
    this.clampPanelToViewport(this.panelTarget)
    this.scrollOverflowIfNeeded()
  }

  onDocPointerdown(event) {
    if (!this.isOpen()) return
    if (this.element.contains(event.target)) return
    if (event.target.closest(".js-word-decoder-letter-input")) return
    this.close()
  }

  autoFollowEnabled() {
    return !this.readSavedPosition() && !this._userMovedPanel
  }

  engineRect() {
    const engineCore = document.querySelector("[data-lexiqamus-word-decoder-target='engineCore']")
    return engineCore?.getBoundingClientRect() || null
  }

  // Unlock home viewport + page scroll once the keyboard actually extends below the viewport.
  releaseHomeViewportLock() {
    if (this._pageScrollEnabled) return
    this._pageScrollEnabled = true
    this.panelTarget.classList.add("lq-grid-kbd--needs-page-scroll")

    const home = document.querySelector(".lexiqamus-home-viewport")
    if (home) {
      home.style.minHeight = ""
      home.style.height    = ""
    }
    document.documentElement.classList.remove("lexiqamus-innovations-scroll", "lexiqamus-landing-scroll-locked")
    document.body.classList.remove("lexiqamus-landing-scroll-locked")
  }

  clampPanelToViewport(panel) {
    const viewportEdgePadding = 8
    const viewportHeight      = window.innerHeight
    const viewportWidth       = window.innerWidth
    const panelWidth          = panel.offsetWidth || 460
    const panelHeight         = panel.offsetHeight || 340
    const currentTop          = parseFloat(panel.style.top) || 0
    const currentLeft         = parseFloat(panel.style.left) || 0
    panel.style.top  = `${Math.max(viewportEdgePadding, Math.min(currentTop, viewportHeight - panelHeight - viewportEdgePadding))}px`
    panel.style.left = `${Math.max(viewportEdgePadding, Math.min(currentLeft, viewportWidth - panelWidth - viewportEdgePadding))}px`
  }

  observeEngineCore() {
    const engineCore = document.querySelector("[data-lexiqamus-word-decoder-target='engineCore']")
    if (!engineCore || typeof ResizeObserver === "undefined") return

    this.resizeObserver = new ResizeObserver(() => {
      if (!this.isOpen() || !this.autoFollowEnabled()) return
      this.positionNear()
    })
    this.resizeObserver.observe(engineCore)
  }

  // Extends document height when the page cannot scroll far enough for the keyboard.
  // Home page: #gridKbdSpacer lives in index_new.html.erb inside .lexiqamus-home-viewport.
  kbdSpacerEl() {
    if (this._kbdSpacer) return this._kbdSpacer
    this._kbdSpacer = document.getElementById("gridKbdSpacer")
    if (!this._kbdSpacer) {
      this._kbdSpacer = document.createElement("div")
      this._kbdSpacer.id = "gridKbdSpacer"
      this._kbdSpacer.style.cssText = "height:0;width:1px;"
      document.body.appendChild(this._kbdSpacer)
    }
    return this._kbdSpacer
  }

  // Reads drag position from localStorage for this keyboard instance.
  readSavedPosition() {
    try {
      const raw = window.localStorage.getItem("lexiqamusGridKbdPosition")
      if (!raw) return null
      const pos = JSON.parse(raw)
      return pos.top && pos.left ? pos : null
    } catch (_e) {
      return null
    }
  }
}
